<template>
  <div>
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="$router.push('/planning')"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn
          v-show="planningDetail"
          ref="save"
          class="capitalize ctrl-button right q-mr-md"
          :color="readOnly ? 'grey-5' : 'ctrl-turquoise'"
          :label="readOnly ? getString('action.edit') : getString('action.save')"
          @click="saveChanges"
          :icon-right="readOnly ? 'edit' : 'save'"
        />
        <q-btn
          ref="add"
          v-show="!adding"
          class="ctrl-button right q-mr-md"
          :color="'positive'"
          no-caps
          :label="getString(null, 'Add a planning', 'Planning toevoegen')"
          @click="add"
          :icon-right="'add'"
        />
        <q-btn
          v-if="planningDetail && planningDetail.id"
          class="q-mr-md capitalize ctrl-button right"
          icon-right="delete"
          color="negative capitalize"
          :label="$store.state.stringsStore.strings.action.delete"
          align="right"
          @click="removePlanning()"
        />
      </div>
    </div>

    <!-- Loader -->
    <div class="loader" v-if="!loaded">
      <q-spinner color="grey-5" :size="80" />
    </div>

    <!-- Main section -->
    <div v-else class="row gutter-sm q-mb-lg">
      <!-- Top section with course specific detail (readonly) -->

      <!-- Left column -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
        <div class="full-width">
          <p class="card-title">Planning - {{ course.title }}</p>
          <!-- <div class="card-block"> -->
          <table-overview
            :endpoint="'planning'"
            :responseFormat="'plannings'"
            :noDataFormat="'planning'"
            :columns="columns"
            customEvents
            @click="fetchPlanning"
            :permission="'planning'"
            :module="'planning'"
            :selectable="true"
          >
          </table-overview>
          <!-- </div> -->
        </div>
      </div>

      <!-- Right column -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
        <div class="full-width q-mb-md">
          <p class="card-title">
            {{
              adding
                ? getString(null, 'Add planning', 'Planning toevoegen')
                : getString(null, 'Planning detail', 'Planning detail')
            }}
          </p>
          <div class="card-block scroll">
            <transition name="fade" mode="out-in">
              <div v-if="planningDetail">
                <div class="row">
                  <div class="col-md-12 q-pr-md">
                    <q-datetime
                      format24h
                      format="DD/MM/YYYY HH:mm"
                      :disable="readOnly"
                      type="datetime"
                      class="full-width q-mb-md"
                      :float-label="`${getString(null, 'Start date and time', 'Startdatum en tijd')} *`"
                      v-model="planningDetail.datetime_start"
                    />
                  </div>
                </div>
                <div class="row">
                  <div class="col q-mr-sm">
                    <q-select
                      :disable="readOnly"
                      :readonly="readOnly"
                      class="full-width"
                      placeholder="Publish"
                      :float-label="`${getString('course.registration.status')} *`"
                      v-model="planningDetail.status"
                      :options="publicationOptions"
                    />
                  </div>
                  <div class="col q-ml-sm">
                    <!-- Publication date -->

                    <q-field
                      class="full-width"
                      :errorLabel="
                        getString(
                          null,
                          'Required unless saving as concept',
                          'Verplicht tenzij opslaan als concept',
                        )
                      "
                    >
                      <q-datetime
                        format24h
                        format="DD/MM/YYYY HH:mm"
                        class="full-width"
                        :disable="planningDetail.status !== 'published' || readOnly"
                        :readonly="planningDetail.status !== 'published'"
                        :float-label="getString('course.registration.datetime')"
                        v-model="planningDetail.datetime_registration"
                        type="datetime"
                      />
                    </q-field>
                  </div>
                </div>
                <chips-autocomplete
                    :key="`${planningDetail.id}-${planningDetail.instructors.length}-${instructorKey}`"
                    class="q-pb-md"
                    :disable="readOnly"
                    :selection="[
                      ...planningDetail.instructors,
                      ...planningDetail.external_instructors.map(i => ({
                        name: `${i.firstname} ${i.lastname}`,
                        value: `${i.firstname} ${i.lastname}`,
                      })),
                    ]"
                    :name="`${getString('course.instructors')} *`"
                    :labelTerm="'name'"
                    :endpoint="'course/instructor'"
                    :resultLabel="'instructors'"
                    customPost
                    :promptNewMessage="getString(null, 'Add extrenal instructor', 'Externe docent toevoegen')"
                    :errorLabel="getString(null, 'Select at least one instructor', 'Selecteer minimaal één instructeur')"
                    @add="promptCustomInstructor"
                    @removed="removeExternalInstructor"
                />

                <div class="row">
                  <q-field class="q-mb-md full-width">
                    <q-search
                      :disable="course.online || readOnly"
                      v-model="planningDetail.location.name"
                      clearable
                      :float-label="
                        `${getString('block.type.button.location')}${
                          course.online ? ' (Online)' : ''
                        }`
                      "
                    >
                      <q-autocomplete
                        value-field="label"
                        :max-results="25"
                        :min-characters="2"
                        @search="autocompleteLocation"
                        @selected="selectLocation"
                      />
                    </q-search>
                  </q-field>
                </div>

                <div class="row">
                  <q-input
                    type="number"
                    class="full-width q-mb-md"
                    :disable="readOnly"
                    :float-label="getString('module.planning.details.max_participants')"
                    v-model="planningDetail.max_participants"
                  />
                </div>

                <div class="row q-mt-md q-mb-md">
                  <q-field>
                    <q-toggle
                      :disable="readOnly"
                      :readonly="readOnly"
                      v-model="planningDetail.full"
                      color="secondary"
                      :label="getString('module.planning.details.full')"
                    />
                  </q-field>
                </div>
              </div>
              <p v-else class="q-pt-md">
                {{
                  getString(
                    null,
                    'Select a planning from the table or add a new planning',
                    'Selecteer een planning of een niuewe toevoegen',
                  )
                }}
              </p>
            </transition>
          </div>
        </div>

        <div class="full-width">
          <p class="card-title">Course detail</p>
          <div class="card-block scroll">
            <q-field class="full-width">
              <q-input
                disable
                readonly
                type="text"
                :float-label="getString('title')"
                v-model="course.title"
              />
            </q-field>
            <q-field class="full-width">
              <q-input readonly disabled type="text" float-label="Type" v-model="course.type" />
            </q-field>
            <q-field class="full-width">
              <q-input
                :disable="readOnly"
                :readonly="readOnly"
                type="text"
                :float-label="getString(null, 'Sort', 'Soort')"
                v-model="course.sort"
              />
            </q-field>

            <chips-autocomplete
              class="q-pb-md"
              disable
              readonly
              :selection="[
                ...course.instructors,
                ...course.external_instructors.map(i => ({ name: `${i.firstname} ${i.lastname}` })),
              ]"
              :name="getString('course.instructors')"
              :labelTerm="'name'"
              :endpoint="'course/instructor'"
              :resultLabel="'instructors'"
            />
            <q-field class="full-width">
              <q-input
                disable
                readonly
                type="text"
                :float-label="getString(null, 'Location', 'Locatie')"
                v-model="course.location.name"
              />
            </q-field>
            <q-field class="full-width">
              <q-input
                disable
                readonly
                type="text"
                :float-label="getString(null, 'Duration', 'Opleidingsduur')"
                v-model="courseDuration"
              />
            </q-field>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal to select templates -->
    <q-modal no-esc-dismiss no-backdrop-dismiss v-model="showCustomInstructorModal">
      <div class="q-ma-lg">
        <div class="row">
          <p>{{ getString(null, 'Add extrenal instructor', 'Externe docent toevoegen') }}</p>
        </div>
        <q-field
          :error="$v.newCustomInstructor.firstname.$invalid"
          :error-label="getString(null, 'Firstname is required', 'Voornaam is vereist')"
        >
          <q-input
            :float-label="getString('firstname')"
            v-model="newCustomInstructor.firstname"
          ></q-input>
        </q-field>
        <q-field
          :error="$v.newCustomInstructor.lastname.$invalid"
          :error-label="getString(null, 'Lastname is required', 'Achternaam is vereist')"
        >
          <q-input
            :float-label="getString('surname')"
            v-model="newCustomInstructor.lastname"
          ></q-input>
        </q-field>
        <q-field
          :error="$v.newCustomInstructor.email.$invalid"
          :error-label="
            getString(null, 'A valid email is required', 'Een geldig e-mailadres is vereist')
          "
        >
          <q-input
            :float-label="getString('email')"
            :error="$v.newCustomInstructor.email.$dirty && $v.newCustomInstructor.email.$invalid"
            v-model="newCustomInstructor.email"
          ></q-input>
        </q-field>
        <div class="row q-mt-md" style="display: flex; justify-content: flex-end;">
          <q-btn
            class="capitalize ctrl-button right q-mr-sm"
            color="negative"
            label="Cancel"
            @click="showCustomInstructorModal = false"
          />
          <q-btn
            class="capitalize ctrl-button right q-mr-sm"
            color="ctrl-turquoise"
            label="OK"
            :disable="$v.newCustomInstructor.$anyError"
            @click="addCustomInstructor"
          />
        </div>
      </div>
    </q-modal>
  </div>
</template>

<script>
import moment from 'moment';
import qs from 'qs';
import EventBus from '@/event-bus';
import { required, requiredIf, email, minLength } from 'vuelidate/lib/validators';

import getString from '@/helpers/GetStrings';
import ChipsAutocomplete from '@/components/ChipsAutocomplete.vue';
import TableOverview from '@/components/overview/TableOverview.vue';

import college from '@/plugins/api/college/college';

export default {
  validations: {
    newCustomInstructor: {
      email: {
        email,
        required: requiredIf(function () {
          return this.showCustomInstructorModal;
        }),
      },
      firstname: {
        required: requiredIf(function () {
          return this.showCustomInstructorModal;
        }),
      },
      lastname: {
        required: requiredIf(function () {
          return this.showCustomInstructorModal;
        }),
      },
    },
  },
  components: {
    TableOverview,
    ChipsAutocomplete,
  },
  mixins: [college],
  data() {
    return {
      instructorKey: 1,
      filters: {
        course_id: '',
      },
      data: {
        instructors: []
      },
      loaded: false,
      readOnly: true,
      course: {},
      plannings: [],
      planningDetail: null,
      rowSelection: [],
      adding: false,
      publicationOptions: [
        {
          label: getString('publication.option.draft'),
          value: 'draft',
        },
        {
          label: getString('publication.option.select'),
          value: 'published',
        },
      ],
      columns: [
        {
          name: '__slot:planning-date',
          title: 'Start',
          sortField: 'datetime_start',
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s text-ellipsis',
        },
        {
          name: '__slot:planning-active',
          title: getString(null, 'Active', 'Actief'),
          titleClass: 'text-left vcol-s',
          dataClass: 'text-left vcol-s text-ellipsis',
        },
        {
          name: '__slot:planning-full',
          title: getString(null, 'Full', 'Vol'),
          sortField: 'full',
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s text-ellipsis',
        },
      ],
      customInstructors: [],
    };
  },

  computed: {
    courseDuration() {
      const unit = this.course.duration.unit;
      return this.course.duration.value + ' ' + getString(`module.course.details.period.${unit}`);
    },
  },

  methods: {
    getString,

    /**
     * Search for location
     */
    autocompleteLocation(terms, done) {
      // Reset location on input
      this.axios.get(`location?autocomplete=${terms}`).then(response => {
        const suggestions = response.data.locations.map(item => ({
          label: item.name,
          id: item.id,
          value: item.id,
        }));
        done(suggestions);
      });
    },

    selectLocation(found, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;
      this.data.location.name = found.label;
      this.data.location.id = found.id;
    },

    removePlanning() {
      // Dialog
      this.$q
        .dialog({
          title: '',
          message: this.getString('module.library.delete.title.single'),
          ok: this.$store.state.stringsStore.strings.dialog.confirm.ok,
          cancel: this.$store.state.stringsStore.strings.dialog.confirm.cancel,
        })
        .then(() => {
          // delete call
          this.deletePlanning(this.planningDetail.id).then(() => {
            this.$q.notify({
              message: this.getString(
                null,
                'Planning successfully removed',
                'Planning succesvol verwijderd',
              ),
              timeout: 2000,
              type: 'positive',
              position: 'top-right',
            });

            EventBus.$emit('refresh-table');

            this.planningDetail = null;
          });
        })
        .catch(() => {
          console.warn('Not removed');
        });
    },

    async add() {
      this.planningDetail = null;
      await new Promise((resolve) => {
        setTimeout(() => resolve(), 200);
      });
      this.adding = true;
      this.readOnly = false;

      EventBus.$emit('refresh-table');

      this.planningDetail = {
        datetime_registration: new Date(),
        datetime_start: new Date(),
        external_instructors: this.course.external_instructors,
        instructors: this.course.instructors,
        full: false,
        max_participants: this.course.max_participants,
        location: this.course.location,
      };
    },

    addCustomInstructor() {
      this.$v.$touch();
      if (this.$v.newCustomInstructor.$anyError) {
        return;
      }
      this.customInstructors.push(this.newCustomInstructor);
      this.planningDetail.instructors.push({ name: `${this.newCustomInstructor.firstname} ${this.newCustomInstructor.lastname}`, value: 'exetrnal' });
      this.instructorKey += 1;
      this.showCustomInstructorModal = false;
    },

    async fetchPlanning(rowData) {
      this.adding = false;
      this.readOnly = true;
      this.planningDetail = null;
      await new Promise((resolve) => {
        setTimeout(() => resolve(), 200);
      });
      this.getPlanningById(rowData.row.id)
        .then(res => {
          this.planningDetail = res.planning;
        })
        .catch(() => {});
    },

    getCourseData(id) {
      return this.axios
        .get(`course/${id}`)
        .then(res => {
          this.course = res.data.course;
        })
        .catch(err => {});
    },

    getPlanningById(id) {
      return this.axios
        .get(`planning/${id}`)
        .then(res => {
          this.planningDetail = res.data.planning;
          this.customInstructors = res.data.planning.external_instructors;
        })
        .catch(err => {});
    },

    saveNewPlanning() {
      return this.axios
        .post('planning', this.getParams(this.planningDetail))
        .then(() => {
          this.adding = false;
          this.readOnly = true;

          EventBus.$emit('refresh-table');

          this.$q.notify({
            message: this.getString(null, 'Planning saved', 'Planning opgeslagen'),
            type: 'positive',
            timeout: 1200,
            position: 'top-right',
          });
        })
        .catch(() => {
          this.$q.notify({
            message: this.getString(
              null,
              'Check for missing fields and try again',
              'Controleer alle velden en probeer het opnieuw',
            ),
            type: 'negative',
            timeout: 4000,
            position: 'top-right',
          });
        });
    },

    saveExistingPlanning() {
      return this.axios
        .put(`planning/${this.planningDetail.id}`, this.getParams(this.planningDetail))
        .then(() => {
          this.$q.notify({
            message: this.getString(null, 'Planning saved', 'Planning opgeslagen'),
            type: 'positive',
            timeout: 1200,
            position: 'top-right',
          });
          this.readOnly = true;
          EventBus.$emit('refresh-table');
        })
        .catch(() => {
          this.$q.notify({
            message: this.getString(
              null,
              'Check for missing fields and try again',
              'Controleer alle velden en probeer het opnieuw',
            ),
            type: 'negative',
            timeout: 4000,
            position: 'top-right',
          });
        });
    },

    getParams(data) {
      let instructors = data.instructors;
      // if external instructors and resource instructors are empty, use the course instructors
      if (!instructors.length && !data.external_instructors.length) {
        instructors = this.course.instructors;
        this.customInstructors = this.course.external_instructors;
      }

      const params = {
        course: this.course.id,
        full: data.full ? 1 : 0,
        status: data.status,
        max_participants: data.max_participants,
        location_id: data.location.id || this.course.location.id,
        instructor: instructors
          .filter(instructor => instructor.value !== 'external')
          .map(instructor => instructor.id),
        datetime_start: data.datetime_start
          ? moment(data.datetime_start)
              .utc()
              .format('YYYY-MM-DD HH:mm:ss')
          : null,
        datetime_registration: data.datetime_registration
          ? moment(data.datetime_registration)
              .utc()
              .format('YYYY-MM-DD HH:mm:ss')
          : null,
      };

      // Manually add custom instructor data to keep array indices
      this.customInstructors.forEach((instructor, index) => {
        params[`external_instructor[${index}][firstname]`] = instructor.firstname;
        params[`external_instructor[${index}][lastname]`] = instructor.lastname;
        params[`external_instructor[${index}][email]`] = instructor.email;
      });

      return qs.stringify(params, { skipNulls: true });
    },

    saveChanges() {
      if (this.readOnly) {
        this.readOnly = false;
        return;
      }

      if (this.adding) {
        this.saveNewPlanning();
      } else {
        this.saveExistingPlanning();
      }
    },
  },

  async mounted() {
    if (this.$route.params.id) {
      this.$store.state.filtersStore.filters[this.$route.name] = {
        course_id: this.$route.params.id,
      };
      try {
        await this.getCourseData(this.$route.params.id);
        // await this.getPlanningsByCourse(this.$route.params.id);
      } catch (error) {
        return;
      }
    }

    this.loaded = true;

    EventBus.$on('row-selection:update', payload => {
      this.rowSelection = [];
    });

    EventBus.$on('chips-autocomplete:instructors', (selection) => {
      const resources = selection.filter((instructor) => !!instructor.id);
      this.planningDetail.instructors = resources.map((i) => {
        return {
          name: i.label,
          value: i.id,
          id: i.id,
        }
      });
    });
  },

  destroyed() {
    EventBus.$off('row-selection:update');
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables';
</style>
